export type DelimitedParseResult = {
  rows: string[][];
  delimiter: '\t' | ',';
};

function parseDelimitedLine(line: string, delimiter: '\t' | ','): string[] {
  const out: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    const next = line[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (ch === delimiter && !inQuotes) {
      out.push(current.trim());
      current = '';
      continue;
    }

    current += ch;
  }

  out.push(current.trim());
  return out;
}

export function parseDelimitedText(rawText: string): DelimitedParseResult {
  const normalized = String(rawText || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();

  if (!normalized) {
    return { rows: [], delimiter: ',' };
  }

  const lines = normalized
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const tabScore = lines.reduce((sum, line) => sum + (line.includes('\t') ? 1 : 0), 0);
  const delimiter: '\t' | ',' = tabScore > 0 ? '\t' : ',';

  return {
    rows: lines.map((line) => parseDelimitedLine(line, delimiter)),
    delimiter,
  };
}

export function rowsToObjects(
  rows: string[][],
  defaultHeaders: string[],
  acceptedHeaderAliases: Record<string, string> = {},
): Record<string, string>[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const aliasMap: Record<string, string> = {};
  Object.entries(acceptedHeaderAliases).forEach(([alias, canonical]) => {
    aliasMap[alias.trim().toLowerCase()] = canonical;
  });
  defaultHeaders.forEach((header) => {
    aliasMap[header.trim().toLowerCase()] = header;
  });

  const firstRow = rows[0] || [];
  const firstRowNormalized = firstRow.map((cell) => cell.trim().toLowerCase());
  const headerHitCount = firstRowNormalized.filter((cell) => aliasMap[cell]).length;
  const isHeaderRow = headerHitCount >= Math.max(1, Math.min(2, firstRow.length));

  const headers = isHeaderRow
    ? firstRow.map((cell, index) => aliasMap[cell.trim().toLowerCase()] || defaultHeaders[index] || `col_${index + 1}`)
    : defaultHeaders;

  const dataRows = isHeaderRow ? rows.slice(1) : rows;

  return dataRows
    .map((row) => {
      const record: Record<string, string> = {};
      headers.forEach((header, index) => {
        record[header] = (row[index] || '').trim();
      });
      return record;
    })
    .filter((record) => Object.values(record).some((value) => value.trim() !== ''));
}
